/**
 * Iter 218 — Page principale Workflows.
 * Route : /admin/workflows
 *
 * Liste les workflows + bouton "Nouveau" (depuis template ou vierge) + filtres.
 */
import { useEffect, useMemo, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { Link } from "react-router-dom";
import { Loader2, Plus, Workflow, Play, Pause, Trash2, Copy, ChevronRight, Sparkles, BarChart3, AlertCircle } from "lucide-react";
import PageHeader from "../components/PageHeader";
import { Button } from "../components/ui/button";

const STATUS_BADGE = {
  active: { color: "#22c55e", bg: "bg-emerald-500/15", label: "Actif" },
  draft: { color: "#64748b", bg: "bg-slate-500/15", label: "Brouillon" },
  inactive: { color: "#94a3b8", bg: "bg-slate-400/15", label: "Inactif" },
  paused: { color: "#f59e0b", bg: "bg-amber-500/15", label: "Pausé" },
};

const TRIGGER_LABELS = {
  "lead.created": "📥 Nouveau lead",
  "lead.tag_added": "🏷️ Tag ajouté",
  "lead.score_threshold": "🎯 Seuil score IA",
  "lead.tier_changed": "🔥 Tier changé",
  "lead.converted": "🎉 Client converti",
  "call.ended": "📞 Appel terminé",
  "call.transcribed": "🎙️ Appel transcrit",
  "form.submitted": "📝 Formulaire soumis",
  "stripe.payment_succeeded": "💰 Paiement OK",
  "stripe.payment_failed": "💳 Paiement échoué",
  "webhook.incoming": "🪝 Webhook reçu",
};

export default function WorkflowsPage() {
  const { axiosAuth } = useAuth();
  const api = useMemo(() => axiosAuth(), [axiosAuth]);
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showTemplates, setShowTemplates] = useState(false);

  const reload = async () => {
    setLoading(true);
    try {
      const [w, s, t] = await Promise.all([
        api.get("/workflows"),
        api.get("/workflows/stats/overview"),
        api.get("/workflows/templates/list"),
      ]);
      setItems(w.data?.items || []);
      setStats(s.data);
      setTemplates(t.data?.templates || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); /* eslint-disable-next-line */ }, []);

  const toggleStatus = async (wf) => {
    const newStatus = wf.status === "active" ? "draft" : "active";
    await api.put(`/workflows/${wf.workflow_id}`, { status: newStatus });
    reload();
  };

  const removeWf = async (wf) => {
    if (!window.confirm(`Supprimer le workflow "${wf.name}" et son historique ?`)) return;
    await api.delete(`/workflows/${wf.workflow_id}`);
    reload();
  };

  const fromTemplate = async (tpl) => {
    const r = await api.post(`/workflows/templates/${tpl.template_id}/instantiate`, {});
    setShowTemplates(false);
    reload();
    return r.data;
  };

  const createBlank = async () => {
    const r = await api.post("/workflows", {
      name: "Nouveau workflow",
      description: "",
      status: "draft",
      trigger: { type: "lead.created", config: {} },
      actions: [],
    });
    setShowTemplates(false);
    return r.data?.workflow_id;
  };

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="workflows-page">
      <PageHeader
        title="Workflows"
        subtitle="Automatisez vos processus : déclencheurs, actions, branches conditionnelles, délais. Inspiré de GoHighLevel."
        icon={<Workflow />}
      />

      {/* KPIs */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2.5 mb-5" data-testid="workflows-kpis">
          <Kpi label="Total" value={stats.workflows.total} accent="#f7c935" />
          <Kpi label="Actifs" value={stats.workflows.active} accent="#22c55e" />
          <Kpi label="Brouillons" value={stats.workflows.draft} accent="#94a3b8" />
          <Kpi label="Runs aujourd'hui" value={stats.runs_today.total} accent="#3b82f6" />
          <Kpi label="Succès / Échecs" value={`${stats.runs_today.success} / ${stats.runs_today.failed}`} accent={stats.runs_today.failed > 0 ? "#f43f5e" : "#22c55e"} />
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <Button onClick={() => setShowTemplates(true)} className="bg-violet-500 hover:bg-violet-600 text-white text-xs h-9" data-testid="btn-new-workflow">
          <Plus className="w-3.5 h-3.5 mr-1" /> Nouveau workflow
        </Button>
        <Link to="/admin/workflows/runs" className="text-xs text-slate-400 hover:text-white inline-flex items-center gap-1 ml-2">
          <BarChart3 className="w-3 h-3" /> Voir tous les runs
        </Link>
      </div>

      {/* Liste */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-5 h-5 animate-spin text-violet-400" />
        </div>
      ) : items.length === 0 ? (
        <div className="bg-[#0b0811] border border-white/5 rounded-xl p-12 text-center" data-testid="workflows-empty">
          <Workflow className="w-12 h-12 mx-auto mb-3 text-slate-700" />
          <p className="text-sm text-slate-400 mb-4">Aucun workflow pour l'instant.</p>
          <Button onClick={() => setShowTemplates(true)} className="bg-violet-500 hover:bg-violet-600 text-white text-xs">
            <Sparkles className="w-3 h-3 mr-1" /> Démarrer avec un template
          </Button>
        </div>
      ) : (
        <div className="bg-[#0b0811] border border-white/5 rounded-xl overflow-hidden" data-testid="workflows-list">
          <table className="w-full text-xs">
            <thead className="bg-[#14101c] text-[10px] uppercase text-slate-500">
              <tr>
                <th className="text-left p-3">Nom</th>
                <th className="text-left p-3">Trigger</th>
                <th className="text-left p-3">Actions</th>
                <th className="text-right p-3">Runs</th>
                <th className="text-left p-3">Statut</th>
                <th className="p-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((wf) => {
                const sb = STATUS_BADGE[wf.status] || STATUS_BADGE.draft;
                return (
                  <tr key={wf.workflow_id} className="border-t border-white/5 hover:bg-white/5" data-testid={`wf-row-${wf.workflow_id}`}>
                    <td className="p-3">
                      <Link to={`/admin/workflows/${wf.workflow_id}`} className="text-white font-bold hover:underline" data-testid={`wf-name-${wf.workflow_id}`}>{wf.name}</Link>
                      {wf.description && <div className="text-[10px] text-slate-500 mt-0.5 truncate max-w-md">{wf.description}</div>}
                    </td>
                    <td className="p-3 text-slate-300">{TRIGGER_LABELS[wf.trigger?.type] || wf.trigger?.type}</td>
                    <td className="p-3 text-slate-400">{wf.actions?.length || 0} étape{(wf.actions?.length || 0) > 1 ? "s" : ""}</td>
                    <td className="p-3 text-right">
                      <span className="text-emerald-400 font-bold">{wf.stats?.runs_success || 0}</span>
                      {wf.stats?.runs_failed > 0 && <span className="text-rose-400 ml-2">×{wf.stats.runs_failed}</span>}
                    </td>
                    <td className="p-3">
                      <span className={`text-[10px] px-1.5 py-0.5 rounded font-bold ${sb.bg}`} style={{ color: sb.color }}>{sb.label}</span>
                    </td>
                    <td className="p-3 whitespace-nowrap">
                      <button onClick={() => toggleStatus(wf)} className={`p-1 ${wf.status === "active" ? "text-amber-400 hover:text-amber-300" : "text-emerald-400 hover:text-emerald-300"}`} title={wf.status === "active" ? "Pauser" : "Activer"} data-testid={`wf-toggle-${wf.workflow_id}`}>
                        {wf.status === "active" ? <Pause className="w-3 h-3" /> : <Play className="w-3 h-3" />}
                      </button>
                      <Link to={`/admin/workflows/${wf.workflow_id}`} className="text-blue-400 hover:text-blue-300 p-1 inline-block" title="Éditer">
                        <ChevronRight className="w-3 h-3" />
                      </Link>
                      <button onClick={() => removeWf(wf)} className="text-rose-400 hover:text-rose-300 p-1" title="Supprimer" data-testid={`wf-delete-${wf.workflow_id}`}>
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal templates */}
      {showTemplates && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => setShowTemplates(false)}>
          <div className="bg-[#0b0811] border border-white/10 rounded-xl max-w-3xl w-full max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="templates-modal">
            <div className="p-5 border-b border-white/5 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-bold text-white">Choisir un template</h2>
                <p className="text-xs text-slate-500">Démarre rapidement avec un workflow pré-fait, modifiable ensuite.</p>
              </div>
              <button onClick={createBlank} className="text-xs text-violet-300 hover:text-violet-200 underline" data-testid="create-blank">Créer vide →</button>
            </div>
            <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-3">
              {templates.map((t) => (
                <button key={t.template_id} onClick={() => fromTemplate(t)} className="text-left bg-[#14101c] hover:bg-white/5 border border-white/5 rounded-xl p-4" data-testid={`tpl-${t.template_id}`}>
                  <div className="flex items-start gap-3">
                    <span className="text-3xl">{t.icon}</span>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-bold text-white">{t.name}</div>
                      <div className="text-[11px] text-slate-500 mt-0.5">{t.description}</div>
                      <div className="flex items-center gap-2 mt-2">
                        <span className="text-[9px] px-1.5 py-0.5 rounded bg-violet-500/15 text-violet-300 uppercase font-bold">{t.category}</span>
                        <span className="text-[9px] text-slate-500">{TRIGGER_LABELS[t.trigger?.type] || t.trigger?.type}</span>
                        <span className="text-[9px] text-slate-500">· {t.actions?.length || 0} actions</span>
                      </div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Kpi({ label, value, accent }) {
  return (
    <div className="bg-[#0b0811] border border-white/5 rounded-xl p-3" style={{ borderLeftColor: accent, borderLeftWidth: 3 }}>
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">{label}</div>
      <div className="text-2xl font-bold text-white mt-0.5">{value}</div>
    </div>
  );
}
